// cataloguePowerPlugsData.ts
import { CnoProShopifyMap } from 'content/products/addons/cnoPro';
import { CurrencySymbol } from 'components/shared/CurrencySymbol';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import { POWER_PLUG_IDS } from 'utils/powerPlugAvailability';
import { PowerPlugCardProps } from './PowerPlugCard';

type TFunction = (key: string) => string;

export interface PowerPlugGroup {
  key: string;
  group: string;
  plugs: PowerPlugCardProps[];
}

// Helper function to get Tesla pricing based on country code
const getTeslaPricing = (countryCode: string) => {
  switch (countryCode) {
    case 'AE':
      return <>{CurrencySymbol.aed}24.99/month</>;
    case 'AU':
      return <>{CurrencySymbol.aud}9.9/month</>;
    case 'BG':
      return <>{CurrencySymbol.bgn}14.99/month</>;
    case 'BR':
      return <>{CurrencySymbol.brl}44.9/month</>;
    case 'CA':
      return <>{CurrencySymbol.cad}7.58/month</>;
    case 'CH':
      return <>{CurrencySymbol.ch}7/month</>;
    case 'CL':
      return <>{CurrencySymbol.clp}7990/month</>;
    case 'CN':
      return <>{CurrencySymbol.cny}48/month</>;
    case 'CO':
      return <>{CurrencySymbol.cop}29900/month</>;
    case 'CZ':
      return <>{CurrencySymbol.czk}199/month</>;
    case 'DK':
      return <>{CurrencySymbol.dkk}59/month</>;
    case 'EG':
      return <>{CurrencySymbol.egp}399.99/month</>;
    case 'ME':
      return <>{CurrencySymbol.eur}5.9/month</>;
    case 'AT':
    case 'BE':
    case 'BA':
    case 'HR':
    case 'CY':
    case 'EE':
    case 'FI':
    case 'FR':
    case 'DE':
    case 'GR':
    case 'IE':
    case 'IT':
    case 'XK':
    case 'LV':
    case 'LT':
    case 'LU':
    case 'MT':
    case 'NL':
    case 'PT':
    case 'RS':
    case 'SK':
    case 'SI':
    case 'ES':
      return <>{CurrencySymbol.eur}7.9/month</>;
    case 'GB':
      return <>{CurrencySymbol.gbp}6.9/month</>;
    case 'HK':
      return <>{CurrencySymbol.hkd}58/month</>;
    case 'HU':
      return <>{CurrencySymbol.huf}2990/month</>;
    case 'ID':
      return <>{CurrencySymbol.idr}119000/month</>;
    case 'IL':
      return <>{CurrencySymbol.ils}24.9/month</>;
    case 'IN':
      return <>{CurrencySymbol.inr}699/month</>;
    case 'JP':
      return <>{CurrencySymbol.jpy}1100/month</>;
    case 'KR':
      return <>{CurrencySymbol.krw}9900/month</>;
    case 'KZ':
      return <>{CurrencySymbol.kzt}3990/month</>;
    case 'MX':
      return <>{CurrencySymbol.mxn}149/month</>;
    case 'MY':
      return <>{CurrencySymbol.myr}29.9/month</>;
    case 'NG':
      return <>{CurrencySymbol.ngn}9900/month</>;
    case 'NO':
      return <>{CurrencySymbol.nok}6.9/month</>;
    case 'NZ':
      return <>{CurrencySymbol.nzd}12.9/month</>;
    case 'PE':
      return <>{CurrencySymbol.pen}29.9/month</>;
    case 'PH':
      return <>{CurrencySymbol.php}399/month</>;
    case 'PK':
      return <>{CurrencySymbol.pkr}1900/month</>;
    case 'PL':
      return <>{CurrencySymbol.pln}39.99/month</>;
    case 'QA':
      return <>{CurrencySymbol.qar}24.99/month</>;
    case 'RO':
      return <>{CurrencySymbol.ron}39.99/month</>;
    case 'SA':
      return <>{CurrencySymbol.sar}29.99/month</>;
    case 'SE':
      return <>{CurrencySymbol.sek}99/month</>;
    case 'SG':
      return <>{CurrencySymbol.sgd}9.98/month</>;
    case 'TH':
      return <>{CurrencySymbol.thb}249/month</>;
    case 'TR':
      return <>{CurrencySymbol.try}299.99/month</>;
    case 'TW':
      return <>{CurrencySymbol.twd}220/month</>;
    case 'TZ':
      return <>{CurrencySymbol.tzs}19900/month</>;
    case 'VN':
      return <>{CurrencySymbol.vnd}199000/month</>;
    case 'ZA':
      return <>{CurrencySymbol.zar}149.99/month</>;

    // USD $7.9 tier countries
    case 'AL':
    case 'AM':
    case 'AZ':
    case 'BS':
    case 'BH':
    case 'BB':
    case 'BJ':
    case 'KH':
    case 'CM':
    case 'CI':
    case 'GE':
    case 'GH':
    case 'KE':
    case 'KG':
    case 'LA':
    case 'MD':
    case 'NP':
    case 'SN':
    case 'SR':
    case 'TJ':
    case 'UG':
    case 'UA':
    case 'UZ':
    case 'ZM':
    case 'ZW':
      return <>{CurrencySymbol.usd}7.9/month</>;

    // USD $6.9 tier countries
    case 'IS':
    case 'US':
    case 'AF':
    case 'DZ':
    case 'AO':
    case 'AI':
    case 'AG':
    case 'AR':
    case 'BZ':
    case 'BM':
    case 'BT':
    case 'BO':
    case 'BW':
    case 'VG':
    case 'BN':
    case 'BF':
    case 'CV':
    case 'KY':
    case 'TD':
    case 'CD':
    case 'CG':
    case 'CR':
    case 'DM':
    case 'DO':
    case 'EC':
    case 'SV':
    case 'SZ':
    case 'FJ':
    case 'GA':
    case 'GM':
    case 'GD':
    case 'GT':
    case 'GW':
    case 'GY':
    case 'HN':
    case 'JM':
    case 'JO':
    case 'KW':
    case 'LB':
    case 'LR':
    case 'LY':
    case 'MO':
    case 'MG':
    case 'MW':
    case 'MV':
    case 'ML':
    case 'MR':
    case 'MU':
    case 'FM':
    case 'MN':
    case 'MS':
    case 'MA':
    case 'MZ':
    case 'MM':
    case 'NA':
    case 'NR':
    case 'NI':
    case 'NE':
    case 'MK':
    case 'OM':
    case 'PW':
    case 'PA':
    case 'PG':
    case 'PY':
    case 'RW':
    case 'SC':
    case 'SL':
    case 'SB':
    case 'LK':
    case 'KN':
    case 'LC':
    case 'VC':
    case 'ST':
    case 'TO':
    case 'TT':
    case 'TN':
    case 'TM':
    case 'TC':
    case 'UY':
    case 'VU':
    case 'VE':
    case 'YE':
      return <>{CurrencySymbol.usd}6.9/month</>;

    default:
      return <>{CurrencySymbol.usd}6.9/month</>;
  }
};

const getCnopricing = (store: ShopifyStore) => {
  const resolvedStore = CnoProShopifyMap[store] ? store : ShopifyStore.ROW;
  // TODO: Need CNO Monthly Pricing for AU and CA
  const monthlyPrices = {
    [ShopifyStore.AE]: <>{CurrencySymbol.aed}14.99/month</>,
    [ShopifyStore.EU]: <>{CurrencySymbol.eur}3.99/month</>,
    [ShopifyStore.GB]: <>{CurrencySymbol.gbp}2.99/month</>,
    [ShopifyStore.IN]: <>{CurrencySymbol.inr}349/month</>,
    [ShopifyStore.ROW]: <>{CurrencySymbol.usd}3.99/month</>,
    [ShopifyStore.US]: <>{CurrencySymbol.usd}3.99/month</>,
    [ShopifyStore.AU]: <>{CurrencySymbol.aud}3.99/month</>,
    [ShopifyStore.CA]: <>{CurrencySymbol.cad}4.1/month</>,
    [ShopifyStore.SA]: <>{CurrencySymbol.sar}12.5/month</>,
    [ShopifyStore.MX]: <>{CurrencySymbol.mxn}12.5/month</>,
    [ShopifyStore.ZA]: <>{CurrencySymbol.zar}12.5/month</>,
    [ShopifyStore.JP]: <>{CurrencySymbol.jpy}450/month</>,
  };
  return monthlyPrices[resolvedStore];
};

const teslaSvg = (
  <svg
    width="560"
    height="620"
    viewBox="0 0 560 620"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M0 32C0 14.3269 14.3269 0 32 0H528C545.673 0 560 14.3269 560 32V588C560 605.673 545.673 620 528 620H32C14.3269 620 0 605.673 0 588V32Z"
      fill="url(#paint0_radial_2489_1879)"
    />
    <path
      d="M0 32C0 14.3269 14.3269 0 32 0H528C545.673 0 560 14.3269 560 32V588C560 605.673 545.673 620 528 620H32C14.3269 620 0 605.673 0 588V32Z"
      fill="url(#paint1_radial_2489_1879)"
    />
    <defs>
      <radialGradient
        id="paint0_radial_2489_1879"
        cx="0"
        cy="0"
        r="1"
        gradientUnits="userSpaceOnUse"
        gradientTransform="translate(280 597) rotate(-179.216) scale(475.045 201.691)"
      >
        <stop stopColor="#023183" />
        <stop offset="0.557692" stopColor="#040E27" />
        <stop offset="0.759615" />
        <stop offset="1" />
      </radialGradient>
      <radialGradient
        id="paint1_radial_2489_1879"
        cx="0"
        cy="0"
        r="1"
        gradientUnits="userSpaceOnUse"
        gradientTransform="translate(280 6.64734e-06) rotate(1.29292) scale(576.147 162.258)"
      >
        <stop stopColor="#003287" />
        <stop offset="0.472222" stopColor="#003590" stopOpacity="0.6" />
        <stop offset="1" stopColor="#003590" stopOpacity="0" />
      </radialGradient>
    </defs>
  </svg>
);

export const getCataloguePowerPlugsData = (
  country = 'US',
  handleUHAppClick: (url: string) => void,
  t: TFunction,
  store: ShopifyStore = ShopifyStore.ROW,
): PowerPlugGroup[] => [
  {
    key: 'longevity',
    group: t('powerplugsPage.catalogue.groups.longevity'),
    plugs: [
      {
        plugId: POWER_PLUG_IDS.AFIB_DETECTION,
        title: <>{t('powerplugsPage.catalogue.plugs.afib.title')}</>,
        subtitle: () => {
          switch (country) {
            case 'US':
              return <>{CurrencySymbol.usd}4.9/month</>;
            case 'IN':
              return <>{CurrencySymbol.inr}499/month</>;
            case 'AE':
              return <>{CurrencySymbol.aed}19.99/month</>;
            case 'GB':
              return <>{CurrencySymbol.gbp}4.9/month</>;
            case 'CA':
              return <>{CurrencySymbol.cad}5.7/month</>;
            case 'AU':
              return <>{CurrencySymbol.aud}6.6/month</>;
            case 'NO':
              return <>{CurrencySymbol.nok}4.9/month</>;
            case 'IS':
              return <>{CurrencySymbol.usd}4.9/month</>;
            case 'MC':
              return <>{CurrencySymbol.usd}4.9/month</>;
            case 'AT':
            case 'BE':
            case 'BG':
            case 'HR':
            case 'CY':
            case 'CZ':
            case 'DK':
            case 'EE':
            case 'FI':
            case 'FR':
            case 'DE':
            case 'GR':
            case 'HU':
            case 'IE':
            case 'IT':
            case 'LV':
            case 'LT':
            case 'LU':
            case 'NL':
            case 'PL':
            case 'PT':
            case 'RO':
            case 'SK':
            case 'SI':
            case 'ES':
            case 'SE':
            default:
              return <>{CurrencySymbol.usd}4.9/month</>;
          }
        },
        description: t('powerplugsPage.catalogue.plugs.afib.description'),
        image: 'web_v2/illustrations/powerplugs/afib-card.png',
        alt: t('powerplugsPage.catalogue.plugs.afib.alt'),
        style: {
          className: 'full',
          background: {
            background:
              'radial-gradient(114.96% 93.08% at 72.06% 58.95%, #000 0%, #3C001A 100%)',
          },
        },
        onExplore: () =>
          handleUHAppClick('https://ultrahuman.onelink.me/QqSM/l0jh4ksh'),
      },
      {
        plugId: POWER_PLUG_IDS.CARDIO_ADAPTABILITY,
        title: (
          <>{t('powerplugsPage.catalogue.plugs.cardioAdaptability.title')}</>
        ),
        subtitle: () => {
          switch (country) {
            case 'US':
              return <>{CurrencySymbol.usd}2.9/month</>;
            case 'IN':
              return <>{CurrencySymbol.inr}299/month</>;
            case 'AE':
              return <>{CurrencySymbol.aed}9.99/month</>;
            case 'GB':
              return <>{CurrencySymbol.gbp}2.9/month</>;
            case 'CA':
              return <>{CurrencySymbol.cad}2.9/month</>;
            case 'AU':
              return <>{CurrencySymbol.aud}3.2/month</>;
            case 'NO':
              return <>{CurrencySymbol.nok}2.9/month</>;
            case 'IS':
              return <>{CurrencySymbol.usd}2.9/month</>;
            case 'MC':
              return <>{CurrencySymbol.usd}2.9/month</>;
            case 'AT':
            case 'BE':
            case 'BG':
            case 'HR':
            case 'CY':
            case 'CZ':
            case 'DK':
            case 'EE':
            case 'FI':
            case 'FR':
            case 'DE':
            case 'GR':
            case 'HU':
            case 'IE':
            case 'IT':
            case 'LV':
            case 'LT':
            case 'LU':
            case 'NL':
            case 'PL':
            case 'PT':
            case 'RO':
            case 'SK':
            case 'SI':
            case 'ES':
            case 'SE':
            default:
              return <>{CurrencySymbol.usd}2.9/month</>;
          }
        },
        description: t(
          'powerplugsPage.catalogue.plugs.cardioAdaptability.description',
        ),
        image: 'web_v2/illustrations/powerplugs/cardio-adaptibility.png',
        alt: t('powerplugsPage.catalogue.plugs.cardioAdaptability.alt'),
        style: {
          className: 'full',
          background: {
            background:
              'radial-gradient(114.96% 93.08% at 72.06% 58.95%, #000 0%, #154E1B 100%)',
          },
        },
        onExplore: () =>
          handleUHAppClick('https://ultrahuman.onelink.me/QqSM/9w7cvqkw'),
      },
    ],
  },
  {
    key: 'productivity',
    group: t('powerplugsPage.catalogue.groups.productivity'),
    plugs: [
      {
        plugId: POWER_PLUG_IDS.CAFFEINE_WINDOW,
        title: <>{t('powerplugsPage.catalogue.plugs.caffeineWindow.title')} </>,
        subtitle: t('powerplugsPage.catalogue.free'),
        description: t(
          'powerplugsPage.catalogue.plugs.caffeineWindow.description',
        ),
        image: 'web_v2/powerplugs/images/pp-caffine-window.png',
        alt: t('powerplugsPage.catalogue.plugs.caffeineWindow.alt'),
        style: {
          className: 'half',
          background: {
            background:
              'radial-gradient(111.47% 121.99% at 50.11% 70.17%, rgba(0, 0, 0, 0.40) 0%, rgba(0, 253, 162, 0.30) 100%), #000',
          },
        },
        onExplore: () =>
          handleUHAppClick('https://ultrahuman.onelink.me/QqSM/jmsj5ie6'),
      },
      {
        plugId: POWER_PLUG_IDS.PHASE_ALIGNMENT,
        title: <>{t('powerplugsPage.catalogue.plugs.phaseAlignment.title')} </>,
        subtitle: t('powerplugsPage.catalogue.free'),
        description: t(
          'powerplugsPage.catalogue.plugs.phaseAlignment.description',
        ),
        image: 'web_v2/powerplugs/images/pp-circadian-rythem-2.png',
        alt: t('powerplugsPage.catalogue.plugs.phaseAlignment.alt'),
        style: {
          className: 'half pb-0',
          background: {
            background:
              'radial-gradient(139% 150.68% at 55.13% 100%, rgba(0, 0, 0, 0.40) 0%, #00B6FF 100%), #000',
          },
        },
        onExplore: () =>
          handleUHAppClick('https://ultrahuman.onelink.me/QqSM/d071mszu'),
      },
      {
        plugId: POWER_PLUG_IDS.TESLA_SYNC,
        title: <>{t('powerplugsPage.catalogue.plugs.teslaSync.title')}</>,
        subtitle: () => getTeslaPricing(country),
        description: t('powerplugsPage.catalogue.plugs.teslaSync.description'),
        image: 'web_v2/powerplugs/images/tesla-2.png',
        alt: t('powerplugsPage.catalogue.plugs.teslaSync.alt'),
        style: {
          className: 'half pb-24',
          backgroundSvg: teslaSvg,
        },
        onExplore: () =>
          handleUHAppClick('https://ultrahuman.onelink.me/QqSM/8tauv9mc'),
      },
      {
        plugId: POWER_PLUG_IDS.VITAMIN_D,
        title: <>{t('powerplugsPage.catalogue.plugs.vitaminD.title')} </>,
        subtitle: t('powerplugsPage.catalogue.free'),
        description: t('powerplugsPage.catalogue.plugs.vitaminD.description'),
        image: 'web_v2/powerplugs/images/pp-vitamin-d.png',
        alt: t('powerplugsPage.catalogue.plugs.vitaminD.alt'),
        style: {
          className: 'half',
          background: {
            background:
              'linear-gradient(186deg, #9A8A6F 9.07%, #A94F1C 95.07%), #EEEEEE',
          },
        },
        onExplore: () =>
          handleUHAppClick('https://ultrahuman.onelink.me/QqSM/tj75txgn'),
      },
    ],
  },
  {
    key: 'womensHealth',
    group: t('powerplugsPage.catalogue.groups.womensHealth'),
    plugs: [
      {
        plugId: POWER_PLUG_IDS.CYCLE_OVULATION,
        title: <>{t('powerplugsPage.catalogue.plugs.cycleOvulation.title')}</>,
        subtitle: t('powerplugsPage.catalogue.free'),
        description: t(
          'powerplugsPage.catalogue.plugs.cycleOvulation.description',
        ),
        image: 'web_v2/powerplugs/images/period-tracking-1.png',
        alt: t('powerplugsPage.catalogue.plugs.cycleOvulation.alt'),
        style: {
          className: 'half pb-0',
          background: {
            background:
              'radial-gradient(136.71% 105.04% at 57.41% 85.48%, #000 35.34%, #710007 100%), #710007',
          },
        },
        onExplore: () =>
          handleUHAppClick('https://ultrahuman.onelink.me/QqSM/0q2jswtn'),
      },
      {
        plugId: POWER_PLUG_IDS.CYCLE_OVULATION_PREMIUM,
        title: (
          <>
            {t('powerplugsPage.catalogue.plugs.cycleOvulationPro.titleLine1')}{' '}
            <br />{' '}
            {t('powerplugsPage.catalogue.plugs.cycleOvulationPro.titleLine2')}
          </>
        ),
        subtitle: () => getCnopricing(store),
        description: t(
          'powerplugsPage.catalogue.plugs.cycleOvulationPro.description',
        ),
        image: 'web_v2/powerplugs/images/cno-pro.png',
        alt: t('powerplugsPage.catalogue.plugs.cycleOvulationPro.alt'),
        style: {
          className: 'half pb-0',
          background: {
            background:
              'radial-gradient(109.58% 97.86% at 50% 58.66%, #000 0%, #0A0001 22.3%, #452B7D 100%)',
          },
        },
        onExplore: () =>
          handleUHAppClick('https://ultrahuman.onelink.me/QqSM/q9csmfux'),
      },
      {
        plugId: POWER_PLUG_IDS.PREGNANCY_INSIGHTS,
        title: (
          <>{t('powerplugsPage.catalogue.plugs.pregnancyInsights.title')}</>
        ),
        subtitle: t('powerplugsPage.catalogue.free'),
        description: t(
          'powerplugsPage.catalogue.plugs.pregnancyInsights.description',
        ),
        image: 'web_v2/powerplugs/images/pp-pregnancy-insights.png',
        alt: t('powerplugsPage.catalogue.plugs.pregnancyInsights.alt'),
        style: {
          className: 'half',
          background: {
            background:
              'radial-gradient(69.28% 75.83% at 50.11% 70.17%, rgba(0, 0, 0, 0.40) 0%, rgba(20, 54, 121, 0.70) 100%), #000',
          },
        },
        onExplore: () =>
          handleUHAppClick('https://ultrahuman.onelink.me/QqSM/cgh2ht5x'),
      },
    ],
  },
  {
    key: 'sleep',
    group: t('powerplugsPage.catalogue.groups.sleep'),
    plugs: [
      {
        plugId: POWER_PLUG_IDS.SMART_ALARM,
        title: <>{t('powerplugsPage.catalogue.plugs.smartAlarm.title')} </>,
        subtitle: t('powerplugsPage.catalogue.free'),
        description: t('powerplugsPage.catalogue.plugs.smartAlarm.description'),
        image: 'web_v2/powerplugs/images/smart-alarm-1.png',
        alt: t('powerplugsPage.catalogue.plugs.smartAlarm.alt'),
        style: {
          className: 'half',
          background: {
            background:
              'radial-gradient(69.28% 75.83% at 50.11% 70.17%, rgba(0, 0, 0, 0.00) 0%, #320C0C 100%), #000',
          },
        },
        onExplore: () =>
          handleUHAppClick('https://ultrahuman.onelink.me/QqSM/um2teyew'),
      },
      {
        plugId: POWER_PLUG_IDS.SOCIAL_JETLAG,
        title: <>{t('powerplugsPage.catalogue.plugs.socialJetlag.title')} </>,
        subtitle: t('powerplugsPage.catalogue.free'),
        description: t(
          'powerplugsPage.catalogue.plugs.socialJetlag.description',
        ),
        image: 'web_v2/powerplugs/images/social-jetlag-1.png',
        alt: t('powerplugsPage.catalogue.plugs.socialJetlag.alt'),
        style: {
          className: 'half',
          background: {
            background:
              'radial-gradient(158.11% 158.11% at -10.57% -19.43%, #A9BDE4 0%, rgba(39, 74, 161, 0.70) 56.5%, #161F46 100%), #000',
          },
        },
        onExplore: () =>
          handleUHAppClick('https://ultrahuman.onelink.me/QqSM/23v3o2xf'),
      },
      {
        plugId: POWER_PLUG_IDS.NEW_PARENT,
        title: <>{t('powerplugsPage.catalogue.plugs.newParent.title')}</>,
        subtitle: t('powerplugsPage.catalogue.free'),
        description: t('powerplugsPage.catalogue.plugs.newParent.description'),
        image: 'web_v2/powerplugs/images/new-parent-6.png',
        alt: t('powerplugsPage.catalogue.plugs.newParent.alt'),
        style: {
          className: 'half new-parent filter relative ',
          background: {
            background: `radial-gradient(91.16% 101.91% at 88.84% 88.95%, #928F58 3.37%, #878550 21.17%, #6E7047 36.52%, #243522 66.31%, #102215 80.83%, #01140B 99.15%)`,
          },
        },
        onExplore: () =>
          handleUHAppClick('https://ultrahuman.onelink.me/QqSM/60mi4hcw'),
      },
      {
        plugId: POWER_PLUG_IDS.SCREENTIME,
        title: <>{t('powerplugsPage.catalogue.plugs.screentime.title')} </>,
        subtitle: t('powerplugsPage.catalogue.free'),
        description: t('powerplugsPage.catalogue.plugs.screentime.description'),
        image: 'web_v2/powerplugs/images/screentime-1.png',
        alt: t('powerplugsPage.catalogue.plugs.screentime.alt'),
        style: {
          className: 'half pb-12',
          background: {
            background:
              'radial-gradient(69.28% 75.83% at 50.11% 70.17%, rgba(0, 0, 0, 0.40) 0%, #21295C 100%), #000',
          },
        },
        onExplore: () =>
          handleUHAppClick('https://ultrahuman.onelink.me/QqSM/2k9kbha1'),
      },
      {
        plugId: POWER_PLUG_IDS.SHIFT_WORK,
        title: <>{t('powerplugsPage.catalogue.plugs.shiftWork.title')}</>,
        subtitle: t('powerplugsPage.catalogue.free'),
        description: t('powerplugsPage.catalogue.plugs.shiftWork.description'),
        image: 'web_v2/powerplugs/images/shift-work-3.png',
        alt: t('powerplugsPage.catalogue.plugs.shiftWork.alt'),
        style: {
          className: 'half add-padding shift-work filter',
          background: {
            background:
              'radial-gradient(110.74% 106.22% at 2.29% 4.57%, rgba(145, 164, 189, 0.80) 0%, rgba(57, 89, 134, 0.72) 55.53%, rgba(14, 52, 83, 0.80) 100%)',
          },
        },
        onExplore: () =>
          handleUHAppClick('https://ultrahuman.onelink.me/QqSM/6or0ntua'),
      },
    ],
  },
];
